/**
 * patchReturnDrive — at end of day, stamps the return-home drive
 * fields (stdrivetimeback, eddrivetimeback, drivebackodostart,
 * drivebackodoend) onto the rep's last CallLog of the day. Depends on
 * that visit having synced (or having been adopted from the server).
 *
 * Local-side: writes the same fields onto the visit_task_sessions row
 * so the timesheet rendering reflects the return drive immediately.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import { emit } from '../../utils/eventLog';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface PatchReturnDriveInput {
  visitLocalId: string;
  stdrivetimeback?: string;
  eddrivetimeback?: string;
  // Wire field names MUST match the CallLog serializer / model
  // (DriveBackOdoStart / DriveBackOdoEnd). The prior names
  // `driveodostartback` / `driveodoendback` exist nowhere server-side, so
  // the server silently dropped them — losing the EOD return-drive odo. (#1987)
  drivebackodostart?: number;
  drivebackodoend?: number;
}

export interface PatchReturnDriveResponse {
  calllogid: number;
}

export const patchReturnDriveOp: WriteOp<
  PatchReturnDriveInput,
  PatchReturnDriveInput,
  PatchReturnDriveResponse
> = {
  kind: 'patchReturnDrive',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    await put(STORES.VISIT_TASK_SESSIONS, {
      ...match,
      stdrivetimeback: input.stdrivetimeback ?? match.stdrivetimeback,
      eddrivetimeback: input.eddrivetimeback ?? match.eddrivetimeback,
      drivebackodostart: input.drivebackodostart ?? match.drivebackodostart,
      drivebackodoend: input.drivebackodoend ?? match.drivebackodoend,
    });

    // Event stream (#3530). The drive HOME is a real leg of the rep's day and
    // reached the ledger nowhere, so the event stream ended at the last store
    // and the journey back existed only on the CallLog. Recorded as the same
    // drive_started/drive_ended pair every other drive uses — stamped at the
    // times the rep actually gave, not at the moment the patch happened to run,
    // and marked so a reader can tell the return leg from the ones between
    // stores. Only when both ends are known; a half-stamped drive would leave
    // the pairing broken in a different way.
    if (input.stdrivetimeback && input.eddrivetimeback) {
      const common = {
        store_id: (match.storeId as number) ?? null,
        calllog_id: (match.callLogId as number) ?? null,
      };
      try {
        await emit({
          ...common,
          event_type: 'drive_started',
          event_at: input.stdrivetimeback,
          payload: { return_drive: true, start_odometer: input.drivebackodostart ?? null },
        });
        await emit({
          ...common,
          event_type: 'drive_ended',
          event_at: input.eddrivetimeback,
          payload: { return_drive: true, end_odometer: input.drivebackodoend ?? null },
        });
      } catch {
        /* event emit is best-effort — it must never break the return drive */
      }
    }
  },

  serialize(input, localId) {
    return {
      action: 'patchReturnDrive',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'PATCH',
      payload: input,
      priority: 30,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as PatchReturnDriveInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    const body: Record<string, unknown> = {};
    if (input.stdrivetimeback !== undefined) body.stdrivetimeback = input.stdrivetimeback;
    if (input.eddrivetimeback !== undefined) body.eddrivetimeback = input.eddrivetimeback;
    if (input.drivebackodostart !== undefined) body.drivebackodostart = input.drivebackodostart;
    if (input.drivebackodoend !== undefined) body.drivebackodoend = input.drivebackodoend;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify(body),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;

    return { status: 'success', retriable: false };
  },

  async reconcile() {
    // No server-assigned IDs to stamp.
  },

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type PatchReturnDriveOp = typeof patchReturnDriveOp;
